import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestCreateCollection,
  requestDeleteCollection,
  requestUpdateCollection,
  requestViewAllCollections
} from './collection.actions';
import { requestViewAllProgrammes, requestViewProgramme } from '../programme/programme.actions';
import {
  getCreateCollectionRequest,
  getDeleteCollectionRequest,
  getUpdateCollectionRequest,
  getViewAllCollectionsRequest
} from './collection.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setCollections } from '../../slices/programme/plan/plan.slice';

import { SUCCESS, ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';

export function* performCreateCollectionRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner('Creating Collection'));

    const [endpoint, options] = getCreateCollectionRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.plans[0].externalReference);
    }

    yield put(removeSpinner('Creating Collection'));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewProgramme({ externalReference: input.programmeExternalReference }));
    yield put(requestViewAllCollections({ externalReference: input.programmeExternalReference }));
    yield put(requestViewAllProgrammes({}));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Creating Collection'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForCreateCollectionRequest() {
  yield takeLatest(requestCreateCollection.type, performCreateCollectionRequest);
}

export function* performViewAllCollectionsRequest({ payload }) {
  const { externalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving All Collections'));

    const [endpoint, options] = getViewAllCollectionsRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setCollections(data.collections));
    yield put(removeSpinner('Retrieving All Collections'));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Retrieving All Collections'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewAllCollectionsRequest() {
  yield takeLatest(requestViewAllCollections.type, performViewAllCollectionsRequest);
}

export function* performUpdateCollectionRequest({ payload }) {
  const { input, programmeExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Collection'));

    const [endpoint, options] = getUpdateCollectionRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Updating Collection'));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllCollections({ externalReference: programmeExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Updating Collection'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUpdateCollectionRequest() {
  yield takeLatest(requestUpdateCollection.type, performUpdateCollectionRequest);
}

export function* performDeleteCollectionRequest({ payload }) {
  const { externalReference, programmeExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Deleting Collection'));

    const [endpoint, options] = getDeleteCollectionRequest(externalReference, programmeExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Deleting Collection'));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllCollections({ externalReference: programmeExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Deleting Collection'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForDeleteCollectionRequest() {
  yield takeLatest(requestDeleteCollection.type, performDeleteCollectionRequest);
}

export default function* collectionSaga() {
  yield all([
    watchForCreateCollectionRequest(),
    watchForViewAllCollectionsRequest(),
    watchForUpdateCollectionRequest(),
    watchForDeleteCollectionRequest()
  ]);
}
